import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { 
  Loader2, 
  LogOut, 
  User,
  Search,
  Clock,
  FolderKanban,
  FolderArchive,
  Library,
  ListChecks,
  Settings,
  CreditCard,
  BarChart3,
  Code,
  FileText,
  AlertTriangle,
  Shield,
  HelpCircle,
  MessageCircleQuestion,
  Sparkles,
  Plug,
  Webhook,
  MoreHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Sidebar from '@/components/common/Sidebar';
import SidebarSnoozePopover from '@/components/common/SidebarSnoozePopover';
import NavigationTabs from '@/components/common/NavigationTabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import DotboxLogo from '@/components/common/DotboxLogo';
import NotificationBell from '@/components/common/NotificationBell';
import CreditProjectionPill from '@/components/common/CreditProjectionPill';
import OnboardingTour, { TOUR_VERSION, TOUR_VERSION_STORAGE_KEY } from '@/components/common/OnboardingTour';
import KeyboardShortcutsModal from '@/components/common/KeyboardShortcutsModal';
import ImpersonationBanner from '@/components/common/ImpersonationBanner';
import EmailVerificationBanner from '@/components/common/EmailVerificationBanner';
import WorkspaceCopilotFloating from '@/components/common/WorkspaceCopilotFloating';
import ContactSupportModal from '@/components/common/ContactSupportModal';
import MobileNavSheet from '@/components/common/MobileNavSheet';
import PortalSwitcher from '@/components/common/PortalSwitcher';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import useWebSocket from '@/hooks/useWebSocket';
import useBranding from '@/hooks/useBranding';
import { useLanguage } from '@/contexts/LanguageContext';
import { isImpersonating } from '@/lib/tokenStorage';
import logger from '@/lib/logger';

// Lazy-loaded dashboard pages for code splitting
const OverviewPage = lazy(() => import('@/components/dashboard/OverviewPage'));
const CommandCenterPage = lazy(() => import('@/components/dashboard/CommandCenterPage'));
const FileVaultPage = lazy(() => import('@/components/dashboard/FileVaultPage'));
const AskWorkspacePage = lazy(() => import('@/components/dashboard/AskWorkspacePage'));
const SignalsPage = lazy(() => import('@/components/dashboard/SignalsPage'));
const TemplateLibraryPage = lazy(() => import('@/components/dashboard/TemplateLibraryPage'));
const AiAgentsPage = lazy(() => import('@/components/dashboard/AiAgentsPage'));
const AgentsHistoryPage = lazy(() => import('@/components/dashboard/AgentsHistoryPage'));
const SocialAdsAIPage = lazy(() => import('@/components/dashboard/SocialAdsAIPage'));
const ConnectionsPage = lazy(() => import('@/components/dashboard/ConnectionsPage'));
const EconomistPage = lazy(() => import('@/components/dashboard/EconomistPage'));
const DecisionLabPage = lazy(() => import('@/components/dashboard/DecisionLabPage'));
const IntegrationsPage = lazy(() => import('@/components/dashboard/IntegrationsPage'));
const APIsPage = lazy(() => import('@/components/dashboard/APIsPage'));
// iter199 — SearchPage / ReadingHistoryPage / ListsPage are now lazy-loaded
// from BrowseContentPage (the consolidated 3-tab surface). Their standalone
// routes here just redirect via <Navigate>, so no top-level lazy import is
// needed anymore.
const BrowseContentPage = lazy(() => import('@/components/dashboard/BrowseContentPage'));
const SavedInsightsPage = lazy(() => import('@/components/dashboard/SavedInsightsPage'));
const SettingsPage = lazy(() => import('@/components/dashboard/SettingsPage'));
const ProfilePage = lazy(() => import('@/components/dashboard/ProfilePage'));
const SubscriptionPage = lazy(() => import('@/components/dashboard/SubscriptionPage'));
const PrivacyPage = lazy(() => import('@/components/dashboard/PrivacyPage'));
const FoldersPage = lazy(() => import('@/components/dashboard/FoldersPage'));
const AnalyticsPage = lazy(() => import('@/components/dashboard/AnalyticsPage'));
const SentimentAlertsPage = lazy(() => import('@/components/dashboard/SentimentAlertsPage'));
const EmbedsPage = lazy(() => import('@/components/dashboard/EmbedsPage'));
const EmbedAnalyticsPage = lazy(() => import('@/components/dashboard/EmbedAnalyticsPage'));
const ReportsPage = lazy(() => import('@/components/dashboard/ReportsPage'));

// iter200 — Default sidebar order. All tabs are freely drag-reorderable;
// user's custom order is persisted to preferences.sidebar_order and rendered
// via computeSidebarOrder() below.
//
// iter247 — Integrations + APIs moved here from the top nav (NAV_ITEMS) and
// sit right after Embeds: all three are "connect dotbox to something else".
// They carry a `labelKey` so they keep the es/fr translations they had in the
// top nav, plus an explicit `testId` so `data-testid`s stay stable when the
// label is translated.
const DEFAULT_SIDEBAR = [
  { icon: MessageCircleQuestion, label: 'Ask workspace', path: '/dashboard/ask' },
  { icon: Library, label: 'Browse Content', path: '/dashboard/browse-content' },
  { icon: Sparkles, label: 'Insights', path: '/dashboard/insights' },
  { icon: FileText, label: 'Signal Reports', path: '/dashboard/reports' },
  { icon: AlertTriangle, label: 'Sentiment Alerts', path: '/dashboard/alerts' },
  { icon: FolderKanban, label: 'Projects', path: '/dashboard/projects' },
  { icon: FolderArchive, label: 'File Vault', path: '/dashboard/vault' },
  { icon: BarChart3, label: 'Analytics', path: '/dashboard/analytics' },
  { icon: Code, label: 'Embeds', path: '/dashboard/embeds' },
  { icon: Plug, label: 'Integrations', path: '/dashboard/integrations', labelKey: 'nav.integrations', testId: 'integrations' },
  { icon: Webhook, label: 'APIs', path: '/dashboard/apis', labelKey: 'nav.apis', testId: 'apis' },
  { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  { icon: User, label: 'Profile', path: '/dashboard/profile' },
  { icon: CreditCard, label: 'Subscription', path: '/dashboard/subscription' },
];

const computeSidebarOrder = (savedOrder) => {
  if (!Array.isArray(savedOrder) || savedOrder.length === 0) return DEFAULT_SIDEBAR;
  const byPath = new Map(DEFAULT_SIDEBAR.map((i) => [i.path, i]));
  const seen = new Set();
  const out = [];
  savedOrder.forEach((path) => {
    const item = byPath.get(path);
    if (item && !seen.has(path)) { out.push(item); seen.add(path); }
  });
  DEFAULT_SIDEBAR.forEach((i) => { if (!seen.has(i.path)) out.push(i); });
  return out;
};

const DashboardLayout = () => {
  const { isAuthenticated, isAdmin, loading, user, logout, isTrialExpired, isTrialActive, daysRemaining, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // iter58.4 — Onboarding tour visibility is driven by the server-side
  // `user.has_seen_onboarding` flag (persists per-user across devices).
  // localStorage is intentionally NOT used, so preview/incognito sessions
  // still respect a user's earlier dismissal.
  //
  // iter196 — Tour version key. When we add new steps we bump TOUR_VERSION
  // in OnboardingTour.jsx; users who already completed an OLDER version
  // still see the new tour exactly once (their stored version < current).
  // We keep the server flag as the primary "brand new user" signal and
  // layer the localStorage version on top of it as an "already-seen-this-cut"
  // signal so version bumps don't force the whole tour on a fresh sign-up.
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (user.has_seen_onboarding === false) {
      setShowOnboarding(true);
      return;
    }
    // Existing user — check if they've seen THIS version of the tour.
    let seenVersion = 0;
    try {
      const raw = localStorage.getItem(TOUR_VERSION_STORAGE_KEY);
      seenVersion = raw ? parseInt(raw, 10) || 0 : 0;
    } catch {
      seenVersion = 0;
    }
    setShowOnboarding(seenVersion < TOUR_VERSION);
  }, [user]);

  // Expose a "replay tour" trigger for other components (e.g. the profile menu
  // button) via a lightweight window custom event.
  useEffect(() => {
    const handler = () => setShowOnboarding(true);
    window.addEventListener('dotbox:start-tour', handler);
    return () => window.removeEventListener('dotbox:start-tour', handler);
  }, []);

  const handleOnboardingComplete = useCallback(async () => {
    setShowOnboarding(false);
    try {
      await api.post('/users/me/onboarding-complete');
      await refreshUser();
    } catch (e) {
      // Non-fatal — tour is still dismissed for this session
      logger.debug('Onboarding-complete flag could not be persisted:', e);
    }
  }, [refreshUser]);

  // iter216 — Tour now lives on Command Center (the post-signup landing
  // page). Content Stream was the old landing route; sign-ups + logins
  // both redirect to /dashboard/command-center now (see LoginPage.jsx +
  // RegisterPage.jsx post-signup CTA), so anchoring the tour there
  // avoids the "here's a tour but on the wrong screen" confusion the
  // user reported. Highlight targets that live on OverviewPage (e.g.
  // [data-testid="nlp-input"]) simply no-op via querySelector returning
  // null — the tour keeps working as a centered walkthrough modal.
  const isCommandCenterPage =
    location.pathname === '/dashboard/command-center' ||
    location.pathname === '/dashboard/command-center/';
  const shouldShowOnboarding = showOnboarding && isCommandCenterPage;
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  // iter247 — "More" sheet for mobile, listing every sidebar destination.
  const [showMobileNav, setShowMobileNav] = useState(false);
  const { t } = useLanguage();

  // Custom branding
  useBranding();

  // Real-time WebSocket connection
  useWebSocket();

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { keys: '?', action: () => setShowShortcuts(true) },
    { keys: '/', action: () => { navigate('/dashboard'); setTimeout(() => document.querySelector('[data-testid="nlp-input"]')?.focus(), 200); }},
    { keys: 'Ctrl+K', action: () => { navigate('/dashboard'); setTimeout(() => document.querySelector('[data-testid="nlp-input"]')?.focus(), 200); }},
    { keys: 'g o', action: () => navigate('/dashboard') },
    { keys: 'g s', action: () => navigate('/dashboard/signals') },
    { keys: 'g d', action: () => navigate('/dashboard/data-sources') },
    { keys: 'g i', action: () => navigate('/dashboard/integrations') },
    { keys: 'g a', action: () => navigate('/dashboard/analytics') },
    { keys: 'n', action: () => navigate('/dashboard/signals') },
    { keys: 'r', action: () => document.querySelector('[data-testid="refresh-button"]')?.click() },
    { keys: 'f', action: () => document.querySelector('[data-testid="toggle-filters"]')?.click() },
    { keys: 'Escape', action: () => setShowShortcuts(false) },
  ], !showOnboarding);

  // iter200 — Sidebar badge counters + custom reorder wiring.
  // MUST live above every early return below so all hooks are always
  // called in the same order (rules-of-hooks compliance).
  const [alertsBadge, setAlertsBadge] = useState(0);
  const [reportsBadge, setReportsBadge] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [alertsR, reportsR] = await Promise.all([
          api.get('/sentiment-alerts/unread-count').catch(() => null),
          api.get('/reports/unread-count').catch(() => null),
        ]);
        if (cancelled) return;
        setAlertsBadge(alertsR?.data?.unread_count || 0);
        setReportsBadge(reportsR?.data?.unread_count || 0);
      } catch { /* soft-fail; badges just stay at their last value */ }
    };
    load();
    const t = setInterval(load, 60000);
    // Also refresh whenever another tab updates the counts (e.g. user
    // marks a report opened, or acknowledges an alert on the alerts page).
    const bump = () => load();
    window.addEventListener('dotbox:badges-refresh', bump);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('dotbox:badges-refresh', bump);
    };
  }, []);

  const sidebarItems = useMemo(() => {
    const ordered = computeSidebarOrder(user?.preferences?.sidebar_order);
    return ordered.map((item) => {
      // iter247 — translate only the items that carry a labelKey (the two
      // moved in from the top nav); the rest keep their literal English label.
      const base = item.labelKey ? { ...item, label: t(item.labelKey) } : item;
      if (base.path === '/dashboard/reports') return { ...base, badge: reportsBadge };
      if (base.path === '/dashboard/alerts')  return { ...base, badge: alertsBadge };
      return base;
    });
  }, [user?.preferences?.sidebar_order, alertsBadge, reportsBadge, t]);

  const handleSidebarReorder = async (newPaths) => {
    try {
      await api.patch('/users/profile', {
        preferences: { ...(user?.preferences || {}), sidebar_order: newPaths },
      });
      await refreshUser();
    } catch {
      toast.error('Could not save sidebar order');
    }
  };

  const renderBadgeSlot = (item) => {
    if (item.path !== '/dashboard/alerts' || !alertsBadge) return null;
    return (
      <SidebarSnoozePopover
        onSnoozed={() => {
          setAlertsBadge((n) => Math.max(0, n - 1));
        }}
      >
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-semibold flex items-center justify-center hover:brightness-110 cursor-pointer border-2 border-[#0F1730]"
          data-testid="sidebar-badge-sentiment-alerts"
          aria-label={`${alertsBadge} unread sentiment alerts — click to snooze`}
        >
          {alertsBadge > 9 ? '9+' : alertsBadge}
        </button>
      </SidebarSnoozePopover>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1024]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-red-400 mx-auto mb-4" />
          <p className="text-gray-300">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect expired users to subscription page (only route they can access)
  if (isTrialExpired && !location.pathname.startsWith('/dashboard/subscription')) {
    return <Navigate to="/dashboard/subscription" replace />;
  }

  // Get current tab from URL
  // iter151 — `/data-sources` and `/feeds` are now sub-tabs of the Content
  // Stream page, so they must keep the 'overview' (Content Stream) top-tab
  // highlighted. The sub-tab switching is handled inside OverviewPage.
  const getCurrentTab = () => {
    const path = location.pathname.replace('/dashboard', '') || '/';
    if (path === '/' || path === '') return 'overview';
    if (path.startsWith('/command-center')) return 'command-center';
    if (path.startsWith('/signals')) return 'signals';
    if (path.startsWith('/data-sources')) return 'overview';
    if (path.startsWith('/feeds')) return 'overview';
    if (path.startsWith('/ai-agents')) return 'ai-agents';
    if (path.startsWith('/social-ads-ai')) return 'social-ads-ai';
    if (path.startsWith('/connections')) return 'connections';
    if (path.startsWith('/economist')) return 'economist';
    if (path.startsWith('/decision-lab')) return 'decision-lab';
    // iter247 — `/integrations` and `/apis` are sidebar destinations now, so
    // they deliberately fall through to '' (no top tab highlighted), exactly
    // like every other sidebar page.
    // Return empty string for sidebar pages so Overview tab isn't falsely "active"
    return '';
  };

  const handleTabChange = (value) => {
    if (value === 'overview') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${value}`);
    }
  };

  // Direct click handler for tabs - works even when tab value hasn't changed
  const handleTabClick = (value) => {
    if (value === 'overview') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${value}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0A1024] flex flex-col">
      <ImpersonationBanner />
      <EmailVerificationBanner user={user} />
      {/* Header */}
      <header className="shadow-sm border-b z-50" style={{ backgroundColor: 'var(--color-sidebar, #0F1730)', borderColor: 'var(--color-sidebar, #0F1730)' }}>
        <div className="flex items-center justify-between px-3 md:px-6 py-3">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <DotboxLogo className="h-8" showText={true} variant="dark" />
          </div>

          {/* Navigation Tabs — hidden on mobile, shown on md+
              iter68: driven by NAV_ITEMS in design_tokens.js */}
          <NavigationTabs
            variant="desktop"
            currentValue={getCurrentTab()}
            onTabChange={handleTabChange}
          />

          {/* Notifications + User Menu + Logout */}
          <div className="flex items-center gap-2">
            {/* iter177 — Portal switcher visible only to admin accounts. */}
            <PortalSwitcher side="user" />
            <CreditProjectionPill />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="relative h-10 w-10 rounded-full text-white hover:bg-[#DC2626]"
                  data-testid="user-menu"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.avatar} alt={user?.full_name || user?.email} />
                    <AvatarFallback className="bg-[#DC2626] text-white">
                      {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 
                       user?.email?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  {/* iter177 — Small ADMIN badge on avatar when the current
                      account is an admin, so the admin always knows which
                      account they're using. Doubles as a fast escape hatch:
                      the dropdown item below is one click away. */}
                  {isAdmin && (
                    <span
                      className="absolute -bottom-1 -right-1 bg-[#DC2626] text-white text-[9px] leading-none font-bold px-1 py-0.5 rounded shadow ring-1 ring-white/20"
                      data-testid="admin-avatar-badge"
                    >
                      ADMIN
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="font-medium">{user?.full_name || 'User'}</p>
                    <p className="w-[200px] truncate text-sm text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {/* iter177 — Fast escape back to the Admin portal when the
                    admin is currently browsing their user account. Hidden
                    for regular subscribers. */}
                {isAdmin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      data-testid="user-switch-to-admin-portal"
                    >
                      <Shield className="mr-2 h-4 w-4 text-red-400" />
                      <span>Switch to Admin Portal</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/privacy')}
                  data-testid="open-privacy-menu-item"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  <span>Privacy & Data</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await api.post('/users/me/onboarding-reset');
                      await refreshUser();
                    } catch (e) {
                      // non-fatal — user can still navigate to command center
                      logger.debug('Onboarding-reset flag could not be persisted:', e);
                    }
                    // iter216 — Tour lives on Command Center; navigate there
                    // so the shouldShowOnboarding guard is satisfied.
                    navigate('/dashboard/command-center');
                    // Small delay so Command Center mounts and any highlight
                    // targets that DO live there (future steps) exist.
                    setTimeout(() => window.dispatchEvent(new Event('dotbox:start-tour')), 100);
                  }}
                  data-testid="take-tour-menu-item"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  <span>Take the tour</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-dropdown">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-[#DC2626] gap-2"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation — shown only on mobile
          iter68: driven by NAV_ITEMS in design_tokens.js */}
      <NavigationTabs
        variant="mobile"
        currentValue={getCurrentTab()}
        onTabChange={handleTabChange}
      />

      <div className="flex flex-1">
        {/* Left Sidebar - hidden on mobile */}
        <TooltipProvider>
          {/* iter127 — Sidebar: `overflow-hidden` → `overflow-y-auto` so on
              short viewports (laptop screens < 800px, common with browser
              chrome) nothing gets clipped. Users can still see every icon. */}
          <aside className="hidden md:flex w-16 flex-col items-center py-4 space-y-4 sticky top-0 h-screen overflow-y-auto" style={{ backgroundColor: 'var(--color-sidebar, #0F1730)' }}>
            {/* iter200 — Unified drag-reorderable sidebar. All 14 tabs (top
                + system items) live in one sortable list. Users can drag any
                tab into any position; the order persists to
                preferences.sidebar_order via handleSidebarReorder. Badges
                on Signal Reports + Sentiment Alerts refresh every 60s; the
                Sentiment Alerts numeric badge opens a Snooze popover on
                click (icon click still navigates). */}
            <Sidebar
              items={sidebarItems}
              activePath={location.pathname}
              onNavigate={navigate}
              onOrderChange={handleSidebarReorder}
              renderBadgeSlot={renderBadgeSlot}
            />
          </aside>
        </TooltipProvider>

        {/* Main Content */}
        <main className="flex-1 overflow-auto flex flex-col bg-[#0A1024] pb-16 md:pb-0">
          {/* Trial Expiring Warning Banner */}
          {isTrialActive && daysRemaining <= 7 && daysRemaining > 0 && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between" data-testid="trial-warning-banner">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Your free trial expires in <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong>.
                  Subscribe now to keep uninterrupted access.
                </span>
              </div>
              <button
                onClick={() => navigate('/dashboard/subscription')}
                className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-1 rounded-md flex-shrink-0 transition-colors"
                data-testid="trial-warning-subscribe-btn"
              >
                Subscribe
              </button>
            </div>
          )}
          <div className="flex-1">
            <Suspense fallback={
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-red-400" />
              </div>
            }>
              <Routes>
                <Route path="/" element={<OverviewPage />} />
                <Route path="/command-center" element={<CommandCenterPage />} />
                <Route path="/signals" element={<SignalsPage />} />
                {/* iter173 — Dedicated Template Library sub-route under Signals. */}
                <Route path="/signals/templates" element={<TemplateLibraryPage />} />
                {/* iter151 — Data Sources & Feeds are now sub-tabs of the
                    Content Stream page. OverviewPage reads the URL to
                    activate the correct sub-tab; deep-links & `g d`
                    shortcut keep working. */}
                <Route path="/data-sources" element={<OverviewPage />} />
                <Route path="/feeds" element={<OverviewPage />} />
                <Route path="/ai-agents" element={<AiAgentsPage />} />
                <Route path="/ai-agents/history" element={<AgentsHistoryPage />} />
                <Route path="/social-ads-ai" element={<SocialAdsAIPage />} />
                <Route path="/social-ads-ai/:tab" element={<SocialAdsAIPage />} />
                <Route path="/connections" element={<ConnectionsPage />} />
                <Route path="/economist" element={<EconomistPage />} />
                <Route path="/decision-lab" element={<DecisionLabPage />} />
                <Route path="/decision-lab/:analysisId" element={<DecisionLabPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/apis" element={<APIsPage />} />
                {/* iter199 — Browse Content consolidates Browse Articles +
                    Recently Read + Lists into one tabbed surface. Legacy
                    URLs redirect (with query params) to preserve deep-linking. */}
                <Route path="/browse-content" element={<BrowseContentPage />} />
                <Route path="/search" element={<Navigate to="/dashboard/browse-content" replace />} />
                <Route path="/recent" element={<Navigate to="/dashboard/browse-content?tab=recent" replace />} />
                <Route path="/insights" element={<SavedInsightsPage />} />
                <Route path="/projects" element={<FoldersPage />} />
                <Route path="/vault" element={<FileVaultPage />} />
                <Route path="/ask" element={<AskWorkspacePage />} />
                <Route path="/lists" element={<Navigate to="/dashboard/browse-content?tab=lists" replace />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/alerts" element={<SentimentAlertsPage />} />
                <Route path="/embeds" element={<EmbedsPage />} />
                <Route path="/widgets/:widgetId/analytics" element={<EmbedAnalyticsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
              </Routes>
            </Suspense>
          </div>
          {/* Footer — iter127: doubled vertical padding (py-3 → py-6) for a
              visually thicker footer per user request. Horizontal padding
              unchanged so the copyright + Contact Support link keep the
              same horizontal alignment across breakpoints. */}
          <footer className="border-t border-[#1F2A4D] bg-[#0F1730] px-4 md:px-6 py-6 flex items-center justify-center gap-3 text-xs text-slate-400">
            <span>©2026 dotbox.ai. All rights reserved.</span>
            <span className="text-slate-600">·</span>
            <button
              type="button"
              onClick={() => setShowSupport(true)}
              className="inline-flex items-center gap-1 text-slate-300 hover:text-rose-400 transition-colors"
              data-testid="footer-contact-support"
            >
              <HelpCircle className="h-3 w-3" />
              Contact Support
            </button>
          </footer>
        </main>
      </div>

      {/* Mobile Bottom Nav - shown only on mobile.
          iter247 — the 4 shortcuts plus a "More" button, because the left
          sidebar rail is desktop-only. "More" opens MobileNavSheet, which
          renders the SAME sidebarItems array (custom order + badges), so no
          sidebar destination is phone-unreachable any more. */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50" style={{ backgroundColor: 'var(--color-sidebar, #0F1730)' }}>
        <div className="flex items-center justify-around py-2">
          {[
            { icon: Library, label: 'Browse Content', path: '/dashboard/browse-content' },
            { icon: BarChart3, label: 'Analytics', path: '/dashboard/analytics' },
            { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
            { icon: User, label: 'Profile', path: '/dashboard/profile' },
          ].map(({ icon: Icon, label, path }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-md ${
                location.pathname === path ? 'text-red-400' : 'text-white/60'
              }`}
              data-testid={`mobile-bottom-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px]">{label}</span>
            </button>
          ))}
          <button
            onClick={() => setShowMobileNav(true)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-md ${
              showMobileNav ? 'text-red-400' : 'text-white/60'
            }`}
            data-testid="mobile-bottom-nav-more"
            aria-label="More destinations"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-[10px]">More</span>
          </button>
        </div>
      </div>

      <MobileNavSheet
        open={showMobileNav}
        onOpenChange={setShowMobileNav}
        items={sidebarItems}
        activePath={location.pathname}
        onNavigate={navigate}
      />

      {/* Onboarding Tour - shown once for new users on the Command Center page only
          (iter216 — Command Center is the post-signup landing route).
          Suppressed during admin impersonation and on subroutes like /settings. */}
      {shouldShowOnboarding && !isImpersonating() && (
        <OnboardingTour onComplete={handleOnboardingComplete} />
      )}

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {/* iter85 — Contact Support modal, opened from footer link.
          Auth state is lifted here so the modal stays context-agnostic. */}
      <ContactSupportModal
        open={showSupport}
        onOpenChange={setShowSupport}
        initialEmail={user?.email || ''}
        emailLocked
      />

      {/* iter106.4 — Workspace Copilot floating action button (hidden on /ask, /settings, /subscription) */}
      <WorkspaceCopilotFloating />
    </div>
  );
};

export default DashboardLayout;
